/* Shell principal de l'application Factumis.
   Layout est le wrapper de tous les écrans protégés (voir <Route element={<Layout />}>
   dans App.tsx). Il orchestre :
   - la Sidebar desktop (collapsable, état persisté via la prop collapsed).
   - la MobileSheet (drawer de navigation mobile, contrôlé par mobileOpen).
   - la Topbar (titre de page, recherche, cloche, thème).
   - le thème (préférence 'system' | 'light' | 'dark', persistée dans localStorage
     sous 'factumis-theme' ; 'system' = clé absente, on suit prefers-color-scheme).
   Les écrans eux-mêmes sont rendus via <Outlet /> dans la zone .content. */
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar, MobileSheet } from './Sidebar';
import { Topbar, type ThemePref } from './Topbar';
import { MeProvider } from '../lib/useMe';
import { TITLES, activeNavId } from './nav';

function initialPref(): ThemePref {
  try {
    const t = localStorage.getItem('factumis-theme');
    if (t === 'dark' || t === 'light') return t;
  } catch {
    /* ignore */
  }
  // Défaut : suivre le thème de l'OS (aligné sur le script anti-flash d'index.html).
  return 'system';
}

const DARK_MQ = '(prefers-color-scheme: dark)';

export function Layout() {
  const [pref, setPref] = useState<ThemePref>(initialPref);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.(DARK_MQ).matches ?? false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Suit les bascules clair/sombre de l'OS tant que la préférence est 'system'.
  useEffect(() => {
    const mq = window.matchMedia?.(DARK_MQ);
    if (!mq) return;
    const onChange = (ev: MediaQueryListEvent) => setSystemDark(ev.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      // 'system' = pas de clé : un choix explicite antérieur est effacé.
      if (pref === 'system') localStorage.removeItem('factumis-theme');
      else localStorage.setItem('factumis-theme', pref);
    } catch {
      /* ignore */
    }
  }, [pref, theme]);

  // Au changement de route (SPA) : met à jour le titre de la page et déplace le
  // focus sur le contenu principal — sinon un utilisateur clavier/lecteur d'écran
  // reste « coincé » sur l'ancien contexte et la nouvelle page n'est pas annoncée.
  useEffect(() => {
    const title = TITLES[activeNavId(pathname)]?.[0];
    document.title = title ? `${title} · Factumis` : 'Factumis';
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    // MeProvider : un seul GET /api/me partagé par la Topbar et tous les écrans.
    <MeProvider>
      <div className={'app' + (collapsed ? ' collapsed' : '')}>
        <Sidebar collapsed={collapsed} toggleCollapse={() => setCollapsed((v) => !v)} />
        <MobileSheet open={mobileOpen} onClose={() => setMobileOpen(false)} />
        <div className="main">
          <Topbar
            themePref={pref}
            cycleTheme={() => setPref((p) => (p === 'system' ? 'light' : p === 'light' ? 'dark' : 'system'))}
            openMobile={() => setMobileOpen(true)}
          />
          {/* tabIndex=-1 : cible focusable (non tabulable) pour le focus au
              changement de route ; -1 évite de l'insérer dans l'ordre de tabulation. */}
          <main className="content" ref={mainRef} tabIndex={-1}>
            <div className="content__in">
              {/* Frontière de chargement pour les écrans en code-splitting (lazy). */}
              <Suspense fallback={<div className="muted" style={{ padding: 24 }}>Chargement…</div>}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </MeProvider>
  );
}
